<template>
  <div class="content">
    <h1 class="has-text-centered">Kapazität eines Kondensators berechnen</h1>

    <div class="has-text-centered" v-if="calculators.length > 1">
      <label class="label">Welche Formel möchtest du benutzen?</label>
      <div class="field pb-2">
        <div class="dropdown is-300px-width" :class="{ 'is-active': dropdown_active }">
          <div class="dropdown-trigger is-300px-width">
            <button class="button is-fullwidth" @click="toggleDropdown()">
              <span v-if="target_calculator.title != ''">{{ target_calculator.title }}</span>
              <span v-else v-katex="target_calculator.katex_formula"></span>
              <span class="icon is-small">
                <i class="fas fa-angle-down" aria-hidden="true"></i>
              </span>
            </button>
          </div>
          <div class="dropdown-menu is-300px-width">
            <div class="dropdown-content">
              <div class="dropdown-item is-clickable" v-for="(calculator, index) in calculators" :key="index" @click="selectTargetCalculator(index)" v-show="index != target_calculator_index">
                <p v-if="calculator.title != ''">{{ calculator.title }}</p>
                <p v-else v-katex="calculator.katex_formula"></p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <DefaultCalculator v-for="(calculator, index) in calculators" :key="index" :prop_katex_formula="calculator.katex_formula" :prop_parser_formula="calculator.parser_formula" :prop_variables="calculator.variables" :prop_id="calculator.id" :prop_target_calculator_index="target_calculator_index" :prop_target_variable="getTargetVariable()" v-show="target_calculator_index == index" />

    <FeedbackBox />

    <GeneralAffiliateBox />

    <div class="box">
      <h2>Was ist die elektrische Kapazität?</h2>
      <p>
        Ein Kondensator hat die Fähigkeit elektrische Ladung zu speichern. Diese gespeicherte Ladung ist die Kapazität welche in der Einheit Farad (F) gemessen wird.
      </p>
      <p>
        Jeder Kondensator kann nur eine begrenzte Menge an Ladung speichern - hat also eine maximale Kapazität. Dies maximale Kapazität wird durch die baulichen Eigenschaften eines Kondensators bestimmt.
      </p>

      <h2>Wie berechnet man die Kapazität eines Kondensators?</h2>
      <p>
        Die Kapazität eines Kondensators lässt sich über mehrere Formeln berechnen.
      </p>

      <h3>Allgemeine Formel für die Kapazität</h3>
      <FormulaDescription :katex_formula="calculators[0].katex_formula" :variables="calculators[0].variables" />

      <h3>Formel für die Kapazität eines Plattenkondensators</h3>
      <FormulaDescription :katex_formula="calculators[1].katex_formula" :variables="calculators[1].variables" />

      <h3>Formel für die Kapazität eines Kugelkondensators</h3>
      <FormulaDescription :katex_formula="calculators[2].katex_formula" :variables="calculators[2].variables" />

      <h2>Weiterführende Links</h2>
      <p>
        <ul class="fa-ul pb-3">
          <li>
            <span class="fa-li"><i class="fas fa-arrow-circle-right"></i></span>
            <a href="https://de.wikipedia.org/wiki/Kondensator_(Elektrotechnik)#Berechnung_der_Kapazit%C3%A4t" target="_blank">Wikipedia-Artikel</a>
          </li>
        </ul>
      </p>

    </div>
  </div>
</template>

<script>
import DefaultCalculator from '@/components/DefaultCalculator.vue'
import FormulaDescription from '@/components/FormulaDescription.vue'
import FeedbackBox from '@/components/FeedbackBox.vue'
import GeneralAffiliateBox from '@/components/GeneralAffiliateBox.vue'

export default {
  name: 'calculator-capacitor-capacitance',
  components: {
    DefaultCalculator,
    FormulaDescription,
    FeedbackBox,
    GeneralAffiliateBox
  },
  metaInfo: {
    title: 'Kondensator Kapazität einfach online berechnen',
    meta: [
      {
        name: 'description',
        content: 'Ein Kondensator hat die Fähigkeit elektrische Ladung zu speichern. Diese gespeicherte Ladung ist die Kapazität. Hier einfach online berechnen.'
      }
    ]
  },
  created () {
    this.$store.commit('updateNavTitle', { title: '<span class="fas fa-angle-left"></span> Zurück', route: 'calculators' })
    this.target_calculator_index = this.getTargetCalculator()
  },
  methods: {
    toggleDropdown () {
      if (this.dropdown_active) {
        this.dropdown_active = false
      } else {
        this.dropdown_active = true
      }
    },
    selectTargetCalculator (index) {
      this.target_calculator_index = index
      this.dropdown_active = false
    },
    getValue (calcId, variable) {
      variable = variable.toLowerCase() + '_val'
      const reg = /^[+-]?[0-9]{1,16}(?:\.[0-9]{1,16})?$/
      if (parseInt(this.$route.query.calc, 10) === calcId) {
        for (const key in this.$route.query) {
          if (key === variable && reg.test(this.$route.query[key])) {
            return this.$route.query[key]
          }
        }
      }
      return 1
    },
    getMultiplicator (calcId, variable) {
      variable = variable.toLowerCase() + '_mul'
      const reg = /^[+-]?[0-9]{1,16}(?:\.[0-9]{1,16})?$/
      if (parseInt(this.$route.query.calc, 10) === calcId) {
        for (const key in this.$route.query) {
          if (key === variable && reg.test(this.$route.query[key])) {
            return Math.pow(10, this.$route.query[key])
          }
        }
      }
      return 1
    },
    getTargetVariable () {
      for (const i in this.calculators) {
        if (parseInt(this.$route.query.calc, 10) === this.calculators[i].id) {
          for (const key in this.$route.query) {
            if (key === 'target_var') {
              for (const y in this.calculators[i].variables) {
                if (this.calculators[i].variables[y].variable.toLowerCase() === this.$route.query[key]) {
                  return this.calculators[i].variables[y].label
                }
              }
            }
          }
        }
      }
      return null
    },
    getTargetCalculator () {
      for (const i in this.calculators) {
        if (parseInt(this.$route.query.calc, 10) === this.calculators[i].id) {
          return parseInt(i)
        }
      }
      return 0
    }
  },
  computed: {
    target_calculator: function () {
      return this.calculators[this.target_calculator_index]
    }
  },
  data () {
    return {
      dropdown_active: false,
      target_calculator_index: 0,
      calculators: [
        // ==== Calculator 1 ====
        {
          id: 1,
          title: '',
          katex_formula: 'C = \\frac{Q}{U}',
          parser_formula: 'C-(Q/U)',
          variables: [
            {
              variable: 'C',
              unit: {
                content: 'F',
                type: 'katex',
                has_prefix: true
              },
              label: {
                short: [
                  {
                    content: 'C',
                    type: 'text'
                  }
                ],
                description: [
                  {
                    content: 'Kapazität in Farad [F]',
                    type: 'text'
                  }
                ]
              },
              component: 'DefaultUnitInput',
              varValue: {
                unitValue: this.getValue(1, 'C'),
                unitMultiplicator: this.getMultiplicator(1, 'C')
              },
              is_constant: false
            },
            {
              variable: 'Q',
              unit: {
                content: 'C',
                type: 'katex',
                has_prefix: true
              },
              label: {
                short: [
                  {
                    content: 'Q',
                    type: 'text'
                  }
                ],
                description: [
                  {
                    content: 'Elektrische Ladung in Coloumb [C]',
                    type: 'text'
                  }
                ]
              },
              component: 'DefaultUnitInput',
              varValue: {
                unitValue: this.getValue(1, 'Q'),
                unitMultiplicator: this.getMultiplicator(1, 'Q')
              },
              is_constant: false
            },
            {
              variable: 'U',
              unit: {
                content: 'V',
                type: 'katex',
                has_prefix: true
              },
              label: {
                short: [
                  {
                    content: 'U',
                    type: 'text'
                  }
                ],
                description: [
                  {
                    content: 'Elektrische Spannung in Volt [V]',
                    type: 'text'
                  }
                ]
              },
              component: 'DefaultUnitInput',
              varValue: {
                unitValue: this.getValue(1, 'U'),
                unitMultiplicator: this.getMultiplicator(1, 'U')
              },
              is_constant: false
            }
          ]
        },
        // ==== Calculator 2 ====
        {
          id: 2,
          title: '',
          katex_formula: 'C = \\epsilon_0*\\epsilon_r*\\frac{A}{d}',
          parser_formula: 'C-(e0*er*(A/d))',
          variables: [
            {
              variable: 'C',
              unit: {
                content: 'F',
                type: 'katex',
                has_prefix: true
              },
              label: {
                short: [
                  {
                    content: 'C',
                    type: 'text'
                  }
                ],
                description: [
                  {
                    content: 'Kapazität in Farad [F]',
                    type: 'text'
                  }
                ]
              },
              component: 'DefaultUnitInput',
              varValue: {
                unitValue: this.getValue(2, 'C'),
                unitMultiplicator: this.getMultiplicator(2, 'C')
              },
              is_constant: false
            },
            {
              variable: 'e0',
              unit: {
                content: '\\frac{As}{Vm}',
                type: 'katex',
                has_prefix: false
              },
              label: {
                short: [
                  {
                    content: '\\epsilon_0',
                    type: 'katex'
                  }
                ],
                description: [
                  {
                    content: 'Elektrische Feldkonstante von 8.854187817*10<sup>-12</sup> in [',
                    type: 'text'
                  },
                  {
                    content: '\\frac{As}{Vm}',
                    type: 'katex'
                  },
                  {
                    content: ']',
                    type: 'text'
                  }
                ]
              },
              component: 'DefaultUnitInput',
              varValue: {
                unitValue: 8.854187817 * Math.pow(10, -12),
                unitMultiplicator: 1
              },
              is_constant: true
            },
            {
              variable: 'er',
              unit: {
                content: '',
                type: 'text',
                has_prefix: false
              },
              label: {
                short: [
                  {
                    content: '\\epsilon_r',
                    type: 'katex'
                  }
                ],
                description: [
                  {
                    content: 'Dielektrizitätszahl',
                    type: 'text'
                  }
                ]
              },
              component: 'DefaultUnitInput',
              varValue: {
                unitValue: this.getValue(2, 'er'),
                unitMultiplicator: this.getMultiplicator(2, 'er')
              },
              is_constant: false
            },
            {
              variable: 'A',
              unit: {
                content: 'm^2',
                type: 'katex',
                has_prefix: false
              },
              label: {
                short: [
                  {
                    content: 'A',
                    type: 'katex'
                  }
                ],
                description: [
                  {
                    content: 'Flächeninhalt in [m<sup>2</sup>]',
                    type: 'text'
                  }
                ]
              },
              component: 'DefaultUnitInput',
              varValue: {
                unitValue: this.getValue(2, 'A'),
                unitMultiplicator: this.getMultiplicator(2, 'A')
              },
              is_constant: false
            },
            {
              variable: 'd',
              unit: {
                content: 'm',
                type: 'katex',
                has_prefix: true
              },
              label: {
                short: [
                  {
                    content: 'd',
                    type: 'katex'
                  }
                ],
                description: [
                  {
                    content: 'Plattenabstand in Meter [m]',
                    type: 'text'
                  }
                ]
              },
              component: 'DefaultUnitInput',
              varValue: {
                unitValue: this.getValue(2, 'd'),
                unitMultiplicator: this.getMultiplicator(2, 'd')
              },
              is_constant: false
            }
          ]
        },
        // ==== Calculator 3 ====
        {
          id: 3,
          title: '',
          katex_formula: 'C = \\frac{4*\\pi*\\varepsilon_0*\\varepsilon_r}{\\frac{1}{R_1}-\\frac{1}{R_2}}',
          parser_formula: 'C-((4*pi*e0*er)/((1/R1)-(1/R2)))',
          variables: [
            {
              variable: 'C',
              unit: {
                content: 'F',
                type: 'katex',
                has_prefix: true
              },
              label: {
                short: [
                  {
                    content: 'C',
                    type: 'katex'
                  }
                ],
                description: [
                  {
                    content: 'Kapazität in Farad [F]',
                    type: 'text'
                  }
                ]
              },
              component: 'DefaultUnitInput',
              varValue: {
                unitValue: this.getValue(3, 'C'),
                unitMultiplicator: this.getMultiplicator(3, 'C')
              },
              is_constant: false
            },
            {
              variable: 'pi',
              unit: {
                content: '',
                type: 'text',
                has_prefix: false
              },
              label: {
                short: [
                  {
                    content: '\\pi',
                    type: 'katex'
                  }
                ],
                description: [
                  {
                    content: 'Kreiszahl PI von 3,1415926...',
                    type: 'text'
                  }
                ]
              },
              component: 'DefaultUnitInput',
              varValue: {
                unitValue: Math.PI,
                unitMultiplicator: 1
              },
              is_constant: true
            },
            {
              variable: 'e0',
              unit: {
                content: '\\frac{As}{Vm}',
                type: 'katex',
                has_prefix: false
              },
              label: {
                short: [
                  {
                    content: '\\epsilon_0',
                    type: 'katex'
                  }
                ],
                description: [
                  {
                    content: 'Elektrische Feldkonstante von 8.854187817*10<sup>-12</sup> in [',
                    type: 'text'
                  },
                  {
                    content: '\\frac{As}{Vm}',
                    type: 'katex'
                  },
                  {
                    content: ']',
                    type: 'text'
                  }
                ]
              },
              component: 'DefaultUnitInput',
              varValue: {
                unitValue: 8.854187817 * Math.pow(10, -12),
                unitMultiplicator: 1
              },
              is_constant: true
            },
            {
              variable: 'er',
              unit: {
                content: '',
                type: 'text',
                has_prefix: false
              },
              label: {
                short: [
                  {
                    content: '\\epsilon_r',
                    type: 'katex'
                  }
                ],
                description: [
                  {
                    content: 'Dielektrizitätszahl',
                    type: 'text'
                  }
                ]
              },
              component: 'DefaultUnitInput',
              varValue: {
                unitValue: this.getValue(3, 'er'),
                unitMultiplicator: this.getMultiplicator(3, 'er')
              },
              is_constant: false
            },
            {
              variable: 'R1',
              unit: {
                content: 'm',
                type: 'katex',
                has_prefix: true
              },
              label: {
                short: [
                  {
                    content: 'R_1',
                    type: 'katex'
                  }
                ],
                description: [
                  {
                    content: 'Radius des inneren Zylindermantels in Meter [m]',
                    type: 'text'
                  }
                ]
              },
              component: 'DefaultUnitInput',
              varValue: {
                unitValue: this.getValue(3, 'R1'),
                unitMultiplicator: this.getMultiplicator(3, 'R1')
              },
              is_constant: false
            },
            {
              variable: 'R2',
              unit: {
                content: 'm',
                type: 'katex',
                has_prefix: true
              },
              label: {
                short: [
                  {
                    content: 'R_2',
                    type: 'katex'
                  }
                ],
                description: [
                  {
                    content: 'Radius des äußeren Zylindermantels in Meter [m]',
                    type: 'text'
                  }
                ]
              },
              component: 'DefaultUnitInput',
              varValue: {
                unitValue: this.getValue(3, 'R2'),
                unitMultiplicator: this.getMultiplicator(3, 'R2')
              },
              is_constant: false
            }
          ]
        }
      ]
    }
  }
}
</script>
